import { Row } from '@tanstack/react-table';
import { useCallback } from 'react';
import {
  SeatingSortingFn,
  useSeatingColumnSorting,
} from 'src/tables/common/hooks/useSeatingColumnSorting';
import { ListingWithEvent } from 'src/tables/ListingTable/ListingTable.types';
import { ListingTableColumnId } from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { sortSeatingSection } from 'src/utils/tableUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { Feature, ListingStatus } from 'src/WebApiController';

import { useUserHasFeature } from './useUserHasFeature';

export const useSortListingTableSeatingColumn = (
  sectionType: SectionType,
  canSortAnySubrows = false
) => {
  const hasSortListingsInGroupFeature = useUserHasFeature(
    Feature.SortListingsInGroup
  );

  const hasDetermisticListingGroupSortFeature = useUserHasFeature(
    Feature.ListingTableDeterminsticListingGroupSort
  );

  const sortingWrapper = useCallback(
    (
      rowA: Row<ListingWithEvent | null>,
      rowB: Row<ListingWithEvent | null>,
      columnId: string,
      sortingFn: SeatingSortingFn<ListingWithEvent>
    ) => {
      if (
        rowA.depth + rowB.depth > 0 &&
        !hasSortListingsInGroupFeature &&
        !canSortAnySubrows
      ) {
        return 0;
      }

      if (
        hasDetermisticListingGroupSortFeature &&
        rowA.depth + rowB.depth == 0 &&
        (rowA.original?.isLtGrp || rowB.original?.isLtGrp)
      ) {
        // Root-level listing group rows should have their seating sorted
        // directly against listing seating
        return sortGroupSeatingSection(rowA, rowB, columnId, sortingFn);
      }

      return sortingFn(rowA, rowB, columnId);
    },
    [
      hasSortListingsInGroupFeature,
      canSortAnySubrows,
      hasDetermisticListingGroupSortFeature,
    ]
  );

  return useSeatingColumnSorting<ListingWithEvent>(
    ListingTableColumnId.Seating,
    sectionType,
    sortingWrapper
  );
};

/**
 * Always pull fully sold listing group to the bottom by not changing its position
 *
 * @returns
 */
export const sortGroupSeatingSection = (
  rowA: Row<ListingWithEvent | null>,
  rowB: Row<ListingWithEvent | null>,
  columnId: string,
  sortingFn: SeatingSortingFn<ListingWithEvent>
) => {
  if (!rowA.original?.listing || !rowB.original?.listing) {
    return sortingFn(rowA, rowB, columnId);
  }
  if (
    rowA.original.listing.status === ListingStatus.FullySold ||
    rowB.original.listing.status === ListingStatus.FullySold
  ) {
    return 0;
  }
  return sortSeatingSection(
    rowA.original.listing.seating,
    rowB.original.listing.seating
  );
};
